import { ComponentFactory, ComponentFactoryResolver } from "@angular/core";
import { HealthRecordCardComponent } from "@app/home/cards/health-record-card.component";
import { RxRenewalCardComponent } from "@app/home/cards/rx-renewal-card.component";
import { PharmacyCardComponent } from "@app/home/cards/pharmacy-card.component";
import { MyPcpCardComponent } from "@app/home/cards/my-pcp-card/my-pcp-card.component";
import { ListCardComponent } from "@app/home/cards/list-card/list-card.component";
import { ComponentType } from "@app/component-type";
import { HealthHistoryCardComponent } from "@app/home/cards/health-history-card/health-history-card.component";
import {
  healthHistoryAdultSurveyId,
  healthHistoryPediatricSurveyId,
  SurveyService,
  SurveySummary,
} from "@app/survey/survey.service";
import { UserService } from "@app/core/user.service";
import { User } from "@app/shared/user";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { filter, take } from "rxjs/operators";
import { DownloadAppCardComponent } from "@app/home/cards/download-app-card/download-app-card.component";
import { MembershipService } from "@app/core/membership.service";
import { Membership } from "@app/core/membership";

export class CardsService {
  private _cards$ = new BehaviorSubject<ComponentFactory<ComponentType>[][]>(null);
  readonly cards$ = this._cards$.asObservable().pipe(filter(card => card != null));

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private surveyService: SurveyService,
    private userService: UserService,
    private membershipService: MembershipService
  ) {}

  getCards() {
    let cards: ComponentFactory<ComponentType>[][] = [
      [
        this.componentFactoryResolver.resolveComponentFactory(HealthRecordCardComponent),
        this.componentFactoryResolver.resolveComponentFactory(RxRenewalCardComponent),
        this.componentFactoryResolver.resolveComponentFactory(PharmacyCardComponent),
      ],
      [
        this.componentFactoryResolver.resolveComponentFactory(MyPcpCardComponent),
        this.componentFactoryResolver.resolveComponentFactory(ListCardComponent),
      ],
    ];

    combineLatest(this.userService.user$, this.membershipService.membership$)
      .pipe(take(1))
      .subscribe(([user, membership]: [User, Membership]) => {
        this.getHealthHistorySurvey(user)
          .pipe(take(1))
          .subscribe({
            next: (survey: SurveySummary) => {
              if (!survey.isAlreadyConfirmed()) {
                cards.unshift([
                  this.componentFactoryResolver.resolveComponentFactory(HealthHistoryCardComponent),
                ]);
              } else {
                cards = this.addDownloadAppCardIfB2b(membership, cards);
              }

              this._cards$.next(cards);
            },
            error: () => {
              cards = this.addDownloadAppCardIfB2b(membership, cards);
              this._cards$.next(cards);
            },
          });
      });

    this.userService.getUser();
    this.membershipService.getMembership();
  }

  private addDownloadAppCardIfB2b(
    membership: Membership,
    cards: ComponentFactory<ComponentType>[][]
  ): ComponentFactory<ComponentType>[][] {
    if (membership.isB2b()) {
      cards.unshift([
        this.componentFactoryResolver.resolveComponentFactory(DownloadAppCardComponent),
      ]);
    }

    return cards;
  }

  private getHealthHistorySurvey(user: User): Observable<SurveySummary> {
    if (user.isPediatric()) {
      return this.surveyService.getSurveyById(healthHistoryPediatricSurveyId);
    } else {
      return this.surveyService.getSurveyById(healthHistoryAdultSurveyId);
    }
  }
}
